/**
 * Mobile menu: turns the theme's <main-menu> into a full-screen drill-down drawer.
 *
 * 1. The third level opens as its own screen (the theme expands it in place, with no
 *    setting to change that).
 * 2. Back buttons name their destination, and each screen's heading row has a "Shop all"
 *    link to that collection.
 * 3. The secondary menu and the header's call button become a strip pinned to the bottom.
 * 4. The drawer is full height, slides in from the left and has its own top bar
 *    ("Menu", or a greeting for a logged-in customer, plus close).
 * 5. Brands opens a screen: the brands picked in the header setting "Mobile menu: top
 *    brands" (<template id="easyr-brands-menu">), then every brand A-Z with search and
 *    letter shortcuts, loaded from templates/page.brands-json.liquid on first open.
 * 6. Rows tint while pressed, and the current page's row and its parents are marked.
 * 7. The first screen is grouped under headings: "Categories" (items with a submenu),
 *    "More ways to shop" (other items) and "Help & services" (links moved out of the
 *    strip). Grouping is by item type, so new menu items need no code change.
 *
 * Mobile only (the theme's md breakpoint); without JS, or on desktop, the theme's menu is
 * unchanged. Strings come from locales (sections.header.easyr_*) via data attributes on
 * this script tag, set in sections/header.liquid. Styles: easyr-mobile-nav.css.
 * See docs/VENDOR-EDITS.md.
 */
(() => {
  const menu = document.querySelector('main-menu');
  if (!menu) return;

  const mobile = window.matchMedia('(max-width: 768.98px)');
  const strings = (document.querySelector('script[data-easyr-nav]') || {}).dataset || {};
  const backTo = (title) => (strings.backTo || 'Back to __TITLE__').replace('__TITLE__', title);
  const shopAllLabel = strings.shopAll || 'Shop all';

  menu.classList.add('easyr-drill');

  /* ---- Drawer top bar: title and close ---- */
  const mainContent = menu.querySelector('.main-menu__content');
  const mainToggle = menu.querySelector('.main-menu__toggle');
  if (mainContent && mainToggle) {
    const bar = document.createElement('div');
    bar.className = 'easyr-menu-bar md:hidden';

    const title = document.createElement('p');
    title.className = 'easyr-menu-bar__title';
    title.textContent = strings.menuTitle || 'Menu';
    bar.append(title);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'easyr-menu-bar__close';
    close.setAttribute('aria-label', strings.close || 'Close');
    close.innerHTML =
      '<svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" aria-hidden="true" focusable="false"><path d="M5 19 19 5M5 5l14 14"/></svg>';
    close.addEventListener('click', () => mainToggle.click()); // the theme's own close path
    bar.append(close);

    mainContent.prepend(bar);
  }

  const shopAllLink = (href) => {
    const a = document.createElement('a');
    a.href = href;
    a.className = 'easyr-shop-all';
    a.textContent = shopAllLabel;
    return a;
  };

  const linkTitle = (link) =>
    (link.firstChild && link.firstChild.nodeType === Node.TEXT_NODE
      ? link.firstChild.textContent
      : link.textContent
    ).trim();

  const setBackLabel = (button, label) => {
    const content = button.querySelector('.main-nav__item-content');
    if (!content) return;
    [...content.childNodes].forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    });
    content.append(document.createTextNode(label));
  };

  /* ---- Brands screen ----
     Brands is a plain link in the main menu, so its screen is built here from a separate
     menu chosen in the header settings. It uses the theme's submenu markup, so the theme's
     JS opens, closes and animates it. Toggle and screen are md:hidden. */
  const brandsTemplate = document.getElementById('easyr-brands-menu');
  const brandLinks = brandsTemplate ? [...brandsTemplate.content.querySelectorAll('a')] : [];
  const leafLinks = [...menu.querySelectorAll('.main-nav > li > .main-nav__row--leaf > .main-nav__item--primary')];
  const brandsRowLink =
    leafLinks.find((a) => a.pathname === '/pages/brands') ||
    leafLinks.find((a) => a.textContent.trim().toLowerCase() === 'brands');
  const sampleToggle = menu.querySelector('.main-nav__row > .main-nav__toggle-btn');
  const sampleBack = menu.querySelector('.main-nav__child .js-back');

  if (brandsRowLink && sampleToggle && sampleBack) {
    const item = brandsRowLink.closest('li');
    const row = brandsRowLink.parentElement;
    const rowTitle = brandsRowLink.textContent.trim();
    const sampleTitle = sampleToggle.closest('li').querySelector('.main-nav__item--primary').textContent.trim();
    const id = 'nav-dropdown-easyr-brands';

    item.classList.add('main-nav__item--dropdown', 'easyr-brands');

    const toggle = sampleToggle.cloneNode(true);
    toggle.classList.add('md:hidden');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', id);
    toggle.setAttribute('aria-label', sampleToggle.getAttribute('aria-label').replace(sampleTitle, rowTitle));
    row.append(toggle);

    const screen = document.createElement('div');
    screen.className = 'main-nav__child mega-nav has-motion md:hidden';
    screen.id = id;
    const container = document.createElement('div');
    container.className = 'container';
    const list = document.createElement('ul');
    list.className = 'child-nav';

    const backLi = document.createElement('li');
    backLi.className = 'md:hidden';
    backLi.append(sampleBack.cloneNode(true));
    list.append(backLi);

    const headLi = document.createElement('li');
    headLi.className = 'md:hidden';
    const head = document.createElement('a');
    head.href = brandsRowLink.href;
    head.className = 'main-nav__item child-nav__item large-text main-nav__item-header';
    head.textContent = rowTitle;
    headLi.append(head);
    list.append(headLi);

    // The picked brands, under a "Popular Brands" label, two to a row.
    if (brandLinks.length) {
      const popular = document.createElement('li');
      popular.className = 'easyr-popular';
      const label = document.createElement('p');
      label.className = 'easyr-subhead';
      label.textContent = strings.popularBrands || 'Popular Brands';
      const grid = document.createElement('div');
      grid.className = 'easyr-popular__grid';
      brandLinks.forEach((source) => {
        const a = document.createElement('a');
        a.href = source.getAttribute('href');
        a.className = 'easyr-popular__item';
        a.textContent = source.textContent.trim();
        grid.append(a);
      });
      popular.append(label, grid);
      list.append(popular);
    }

    container.append(list);
    screen.append(container);
    item.append(screen);

    setUpBrandIndex(item, screen, list, brandsRowLink.pathname);
  }

  /* ---- All brands A-Z (inside the Brands screen) ----
     Too large to include in every page, so the list is fetched from the brands JSON feed
     (?view=brands-json, all pages in parallel) on first open and cached for the session. */
  function setUpBrandIndex(item, screen, list, pagePath) {
    const feed = `${pagePath}?view=brands-json`;
    const CACHE = 'easyr-brand-index-v1';
    const here = window.location.pathname.replace(/\/$/, '');

    const heading = document.createElement('li');
    heading.className = 'easyr-az__heading easyr-subhead';
    heading.textContent = strings.allBrands || 'All brands A–Z';
    list.append(heading);

    const searchLi = document.createElement('li');
    searchLi.className = 'easyr-az__search';
    const search = document.createElement('input');
    search.type = 'search';
    search.autocomplete = 'off';
    search.placeholder = strings.brandSearch || 'Search brands';
    search.setAttribute('aria-label', strings.brandSearch || 'Search brands');
    searchLi.append(search);
    list.append(searchLi);

    const lettersLi = document.createElement('li');
    lettersLi.className = 'easyr-az__letters';
    list.append(lettersLi);

    const status = document.createElement('li');
    status.className = 'easyr-az__status';
    status.textContent = strings.loading || 'Loading…';
    list.append(status);

    let loaded = false;
    let rows = [];
    let groups = [];

    const readCache = () => {
      try {
        return JSON.parse(sessionStorage.getItem(CACHE));
      } catch (e) {
        return null;
      }
    };
    const writeCache = (brands) => {
      try {
        sessionStorage.setItem(CACHE, JSON.stringify(brands));
      } catch (e) {
        /* private mode */
      }
    };

    async function fetchAll() {
      const cached = readCache();
      if (cached && cached.length) return cached;
      const page = (n) =>
        fetch(`${feed}&page=${n}`).then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.json();
        });
      const first = await page(1);
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, (first.pages || 1) - 1) }, (_, i) => page(i + 2))
      );
      const seen = new Set();
      const brands = [first, ...rest]
        .flatMap((d) => d.brands || [])
        .filter((b) => b.url && !seen.has(b.url) && seen.add(b.url))
        .map((b) => ({ title: b.title, url: b.url, letter: b.letter || '#' }))
        .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
      writeCache(brands);
      return brands;
    }

    function render(brands) {
      const byLetter = new Map();
      brands.forEach((b) => {
        const key = /^[A-Z]$/.test(b.letter) ? b.letter : '#';
        if (!byLetter.has(key)) byLetter.set(key, []);
        byLetter.get(key).push(b);
      });
      const order = [...byLetter.keys()].sort((a, b) => (a === '#') - (b === '#') || a.localeCompare(b));

      const fragment = document.createDocumentFragment();
      order.forEach((letter) => {
        const head = document.createElement('li');
        head.className = 'easyr-az__letter';
        head.id = `easyr-az-${letter === '#' ? 'num' : letter}`;
        head.textContent = letter;
        fragment.append(head);
        const members = [];
        byLetter.get(letter).forEach((b) => {
          const li = document.createElement('li');
          li.className = 'easyr-az__brand';
          const a = document.createElement('a');
          a.href = b.url;
          a.className = 'main-nav__item child-nav__item';
          a.textContent = b.title;
          if (b.url.replace(/\/$/, '') === here) li.classList.add('easyr-current');
          li.append(a);
          fragment.append(li);
          members.push(li);
          rows.push({ li, name: b.title.toLowerCase() });
        });
        groups.push({ head, members });
      });
      status.remove();
      list.append(fragment);

      order.forEach((letter) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = letter;
        button.addEventListener('click', () => {
          const target = document.getElementById(`easyr-az-${letter === '#' ? 'num' : letter}`);
          if (target) screen.scrollTo({ top: target.offsetTop - lettersLi.offsetHeight, behavior: 'smooth' });
        });
        lettersLi.append(button);
      });
    }

    // Matches the start of any word: "sh" finds "Memphis Shades" but not "Yoshimura".
    search.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(^|[^a-z0-9])${escaped}`);
      list.classList.toggle('easyr-az--filtering', q.length > 0);
      rows.forEach((r) => {
        r.li.hidden = q.length > 0 && !re.test(r.name);
      });
      groups.forEach((g) => {
        g.head.hidden = g.members.every((li) => li.hidden);
      });
    });

    const load = () => {
      if (loaded) return;
      loaded = true;
      fetchAll()
        .then(render)
        .catch(() => {
          loaded = false; // try again next time the screen opens
          status.textContent = strings.brandsFailed || 'Couldn’t load brands.';
        });
    };

    /* Load when the screen opens (the theme adds is-open); start on the first touch of the
       Brands row so the list is usually ready by the time the screen slides in. */
    new MutationObserver(() => {
      if (item.classList.contains('is-open')) load();
    }).observe(item, { attributes: true, attributeFilter: ['class'] });
    item.querySelector(':scope > .main-nav__row').addEventListener('pointerdown', load, { once: true });
  }

  /* ---- Level 2: named Back and "Shop all" on the heading row ---- */
  menu.querySelectorAll('.main-nav__child').forEach((screen) => {
    const back = screen.querySelector('.child-nav > li > .js-back');
    if (back) setBackLabel(back, strings.backToMenu || 'Back to menu');

    const heading = screen.querySelector('.child-nav > li > .main-nav__item-header');
    if (heading && !heading.getAttribute('href').endsWith('#')) {
      heading.parentElement.classList.add('easyr-headrow');
      heading.parentElement.append(shopAllLink(heading.href));
    }
  });

  /* ---- Level 3: build the screen the first time it opens ---- */
  const vendorBack = menu.querySelector('.main-nav__item--back');

  function build(item) {
    const panel = item.querySelector(':scope > .child-nav__panel');
    if (!panel || panel.querySelector(':scope > .easyr-drill__head')) return panel;

    const screen = item.closest('.main-nav__child');
    const parentHeading = screen && screen.querySelector('.child-nav > li > .main-nav__item-header');

    const head = document.createElement('div');
    head.className = 'easyr-drill__head';

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'main-nav__item main-nav__item--back relative easyr-drill__back';
    back.innerHTML = vendorBack ? vendorBack.innerHTML : '<div class="main-nav__item-content text-start"></div>';
    setBackLabel(back, parentHeading ? backTo(linkTitle(parentHeading)) : strings.backToMenu || 'Back to menu');
    head.append(back);

    const link = item.querySelector(':scope > .main-nav__item-content > a.child-nav__item');
    if (link) {
      const row = document.createElement('div');
      row.className = 'easyr-headrow';
      const title = document.createElement('a');
      title.href = link.href;
      title.className = 'main-nav__item child-nav__item large-text main-nav__item-header';
      title.textContent = linkTitle(link);
      row.append(title);
      if (!link.getAttribute('href').endsWith('#')) row.append(shopAllLink(link.href));
      head.append(row);
    }

    panel.prepend(head);
    return panel;
  }

  function open(item) {
    const screen = item.closest('.main-nav__child');
    const panel = build(item);
    if (!screen || !panel) return;

    /* The new screen is positioned against the level-2 screen, so park that at the top
       (and remember where it was, for Back). */
    screen.dataset.easyrScroll = String(screen.scrollTop);
    screen.scrollTop = 0;
    panel.scrollTop = 0;
    screen.classList.add('easyr-drill-active');
    item.classList.add('easyr-drill-open');

    const toggle = item.querySelector(':scope > .main-nav__item-content .js-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');

    const back = panel.querySelector('.easyr-drill__back');
    if (back) setTimeout(() => back.focus({ preventScroll: true }), 200);
  }

  function close(item, restoreFocus = true) {
    if (!item || !item.classList.contains('easyr-drill-open')) return;
    const screen = item.closest('.main-nav__child');
    item.classList.remove('easyr-drill-open');

    const toggle = item.querySelector(':scope > .main-nav__item-content .js-toggle');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) toggle.focus({ preventScroll: true });
    }

    if (screen) {
      screen.classList.remove('easyr-drill-active');
      screen.scrollTop = Number(screen.dataset.easyrScroll || 0);
    }
  }

  function closeAll() {
    menu.querySelectorAll('.easyr-drill-open').forEach((item) => close(item, false));
  }

  /* Capture phase, so this runs before the theme's own click handler on the nav and can
     stop it from expanding the dropdown. */
  menu.addEventListener(
    'click',
    (evt) => {
      if (!mobile.matches) return;

      const back = evt.target.closest('.easyr-drill__back');
      if (back) {
        evt.preventDefault();
        evt.stopPropagation();
        close(back.closest('.child-nav__item--toggle'));
        return;
      }

      const row = evt.target.closest('.child-nav__item--toggle > .main-nav__item-content');
      if (!row) return;

      const onToggle = evt.target.closest('.js-toggle');
      const onLink = evt.target.closest('a.child-nav__item');
      /* Same rule as the theme: the name opens the submenu when "Links toggle dropdown
       menus" is on, or when the link is "#". Otherwise the name navigates. */
      const linkOpens = onLink && (menu.dataset.linkToggle || onLink.getAttribute('href').endsWith('#'));
      if (!onToggle && !linkOpens) return;

      const item = row.parentElement;
      if (!item.querySelector(':scope > .child-nav__panel')) return;

      evt.preventDefault();
      evt.stopPropagation();
      open(item);
    },
    true
  );

  /* Reset when the level-2 screen closes (theme Back, another item opened), and when the
     viewport crosses into desktop. */
  const dropdownObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      if (!record.target.classList.contains('is-open')) {
        record.target.querySelectorAll('.easyr-drill-open').forEach((item) => close(item, false));
      }
    });
  });
  menu.querySelectorAll('.main-nav__item--dropdown').forEach((dropdown) => {
    dropdownObserver.observe(dropdown, { attributes: true, attributeFilter: ['class'] });
  });
  mobile.addEventListener('change', closeAll);

  document.addEventListener(
    'keydown',
    (evt) => {
      if (evt.key !== 'Escape' || !mobile.matches) return;
      const openItem = menu.querySelector('.easyr-drill-open');
      if (openItem) {
        evt.stopPropagation();
        close(openItem);
      }
    },
    true
  );

  /* ---- Pinned strip ---- */
  const ICONS = {
    account: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/>',
    gift: '<rect x="3" y="8" width="18" height="13" rx="1"/><path d="M12 8v13M3 12.5h18M12 8C10.5 5 7 4 7 6.5S10 8 12 8zm0 0c1.5-3 5-4 5-1.5S14 8 12 8z"/>',
    returns: '<path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-3"/>',
    help: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6"/><path d="M12 17.5h.01"/>',
    track:
      '<path d="M3 7h11v10H3zM14 10h4l3 3v4h-7"/><circle cx="7" cy="18" r="1.8"/><circle cx="17.5" cy="18" r="1.8"/>',
    link: '<path d="M9 18l6-6-6-6"/>'
  };
  const iconFor = (href, text) => {
    const s = `${href} ${text}`.toLowerCase();
    if (/account|login|log-in|sign/.test(s)) return 'account';
    if (/gift/.test(s)) return 'gift';
    if (/return|refund/.test(s)) return 'returns';
    if (/track|order/.test(s)) return 'track';
    if (/help|contact|faq|support/.test(s)) return 'help';
    return 'link';
  };

  const strip = menu.querySelector('.secondary-nav-container');
  if (strip) {
    strip.classList.add('easyr-strip');

    /* The announcement bar's links join the strip, ahead of the call button. They are
       copies, so the desktop announcement bar is unchanged; CSS hides the theme's own
       mobile list of them. */
    const list = strip.querySelector('.secondary-nav');
    const cta = list && list.querySelector('.secondary-nav__cta');
    const ctaItem = cta && cta.closest('li');
    menu.querySelectorAll('.mob__announcement-links .secondary-nav__item').forEach((a) => {
      const li = document.createElement('li');
      li.className = 'easyr-strip__extra';
      li.append(a.cloneNode(true));
      list.insertBefore(li, ctaItem || null);
    });
    if (menu.querySelector('.mob__announcement-links .secondary-nav__item'))
      menu.classList.add('easyr-strip-has-extras');

    /* The strip keeps account, help and the call button; every other link moves to a
       list under the categories. Copies again: the originals are only hidden on mobile. */
    const STRIP_TYPES = ['account', 'help'];
    const moreList = document.createElement('ul');
    moreList.className = 'easyr-more-links';
    moreList.setAttribute('role', 'list');

    strip.querySelectorAll('.secondary-nav__item').forEach((a) => {
      if (a.classList.contains('secondary-nav__cta')) return; // call button keeps the theme's icon
      const type = iconFor(a.getAttribute('href') || '', a.textContent);
      if (STRIP_TYPES.includes(type)) {
        a.insertAdjacentHTML(
          'afterbegin',
          `<svg class="easyr-strip__icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICONS[type]}</svg>`
        );
        return;
      }
      const li = document.createElement('li');
      const link = a.cloneNode(true);
      link.className = 'easyr-more-links__item';
      li.append(link);
      moreList.append(li);
      a.closest('li').classList.add('easyr-strip__moved');
    });

    const mainNav = menu.querySelector(
      '.main-menu__content > .main-nav, .main-menu__content nav:not(.secondary-nav-container) > .main-nav'
    );
    const navBlock = mainNav && (mainNav.closest('nav') || mainNav);
    if (moreList.children.length && navBlock) {
      const servicesHead = document.createElement('p');
      servicesHead.className = 'easyr-group';
      servicesHead.textContent = strings.groupServices || 'Help & services';
      navBlock.after(servicesHead, moreList);
    }
  }

  /* Flag the open menu on <html> so fixed widgets (the Gorgias chat button) can be hidden
     while it is open. */
  const content = menu.querySelector('.main-menu__content');
  if (content) {
    let closeTimer;
    let wasVisible = content.classList.contains('is-visible');
    const sync = () => {
      const isOpen = content.classList.contains('is-open');
      const isVisible = content.classList.contains('is-visible');
      document.documentElement.classList.toggle('easyr-menu-open', isOpen);

      /* The theme only removes is-open on an opacity transitionend. If that never fires
         (reduced motion, a background tab), the menu stays half-open, so dispatch it
         ourselves when the real one hasn't arrived. Only while closing (is-visible just
         went away): while opening, is-open also comes before is-visible, two frames later. */
      clearTimeout(closeTimer);
      const closing = isOpen && !isVisible && wasVisible;
      wasVisible = isVisible;
      if (closing) {
        closeTimer = setTimeout(() => {
          if (content.classList.contains('is-open') && !content.classList.contains('is-visible')) {
            content.dispatchEvent(new TransitionEvent('transitionend', { propertyName: 'opacity' }));
          }
        }, 450);
      }
    };
    new MutationObserver(sync).observe(content, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  /* ---- Group headings on the first screen ----
     The headings are list items in the main nav, which is also the desktop nav bar, so
     they carry md:hidden. */
  const navList = menu.querySelector('.main-menu__content .main-nav');
  if (navList) {
    const items = [...navList.children];
    const isCategory = (li) =>
      li.classList.contains('main-nav__item--dropdown') && !li.classList.contains('easyr-brands');
    const heading = (text) => {
      const li = document.createElement('li');
      li.className = 'easyr-group md:hidden';
      li.textContent = text;
      return li;
    };
    const firstCategory = items.find(isCategory);
    const firstOther = items.find((li) => !isCategory(li));
    if (firstCategory) navList.insertBefore(heading(strings.groupCategories || 'Categories'), firstCategory);

    /* Phone-only extra links (header setting "Mobile menu: More ways to shop", rendered
       as <template id="easyr-more-menu">) go first in that group, as plain rows. */
    const moreTemplate = document.getElementById('easyr-more-menu');
    const extraRows = (moreTemplate ? [...moreTemplate.content.querySelectorAll('a')] : []).map((source) => {
      const li = document.createElement('li');
      li.className = 'easyr-more-row md:hidden';
      const row = document.createElement('div');
      row.className = 'main-nav__row main-nav__row--leaf';
      const a = document.createElement('a');
      a.className = 'main-nav__item--primary';
      a.href = source.getAttribute('href');
      a.textContent = source.textContent.trim();
      row.append(a);
      li.append(row);
      return li;
    });
    const groupStart = firstOther || null; // null = append at the end of the list
    if (firstCategory && (firstOther || extraRows.length)) {
      navList.insertBefore(heading(strings.groupMore || 'More ways to shop'), groupStart);
    }
    extraRows.forEach((li) => navList.insertBefore(li, groupStart));
  }

  /* ---- Press feedback ----
     Tints the pressed row for at least 150ms so a quick tap still shows it. pointercancel
     (the finger starts scrolling) clears it immediately. */
  const ROW = [
    '.main-nav__row',
    '.child-nav > li > .main-nav__item-content',
    '.child-nav > li > .child-nav__item--toggle',
    '.child-nav > li > .main-nav__item',
    '.easyr-drill__head > *',
    '.main-nav__grandchild > li',
    '.easyr-more-links > li',
    '.easyr-strip .secondary-nav > li',
    '.easyr-popular__grid > a'
  ].join(', ');
  let pressed = null;
  let pressedAt = 0;
  const release = (immediate) => {
    if (!pressed) return;
    const row = pressed;
    pressed = null;
    const wait = immediate ? 0 : Math.max(0, 150 - (Date.now() - pressedAt));
    setTimeout(() => row.classList.remove('easyr-pressed'), wait);
  };
  if (mainContent) {
    mainContent.addEventListener('pointerdown', (evt) => {
      if (!mobile.matches || evt.button > 0) return;
      const row = evt.target.closest(ROW);
      if (!row || !mainContent.contains(row)) return;
      pressed = row;
      pressedAt = Date.now();
      row.classList.add('easyr-pressed');
    });
    mainContent.addEventListener('pointerup', () => release(false));
    mainContent.addEventListener('pointercancel', () => release(true));
    mainContent.addEventListener('pointerleave', () => release(true));
  }

  /* ---- Current page ----
     Marks the current page's link and the rows that lead to it. Skips "/", so a menu item
     linked to the homepage isn't marked on the home page. */
  const trim = (path) => path.replace(/\/$/, '');
  const here = trim(window.location.pathname);
  if (here) {
    menu.querySelectorAll('.main-menu__content a[href]').forEach((a) => {
      if (a.closest('.easyr-menu-bar, .easyr-strip')) return;
      if (a.matches('.easyr-shop-all, .main-nav__item-header, .main-nav__item--go')) return;
      if (trim(a.pathname) !== here) return;

      (a.closest(ROW) || a).classList.add('easyr-current');

      const panelItem = a.closest('.child-nav__panel') && a.closest('.child-nav__item--toggle');
      const panelRow = panelItem && panelItem.querySelector(':scope > .main-nav__item-content');
      if (panelRow) panelRow.classList.add('easyr-current-trail');

      const top = a.closest('.main-nav > li');
      const topRow = top && top.querySelector(':scope > .main-nav__row');
      if (topRow && !topRow.contains(a)) topRow.classList.add('easyr-current-trail');
    });
  }
})();
